"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = VideoSection;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react2 = require("@linaria/react");
var _patternLibraryUtils = require("@site/pattern-library-utils");
var _useElementProps = _interopRequireDefault(require("@site/pattern-library-utils/hooks/useElementProps"));
var _intents = _interopRequireDefault(require("@site/pattern-library-utils/intents"));
var _section = _interopRequireDefault(require("@site/pattern-library-core/section"));
var _videoLightbox = _interopRequireDefault(require("../video-lightbox"));
var _playButton = _interopRequireDefault(require("../video-lightbox/play-button"));
var _index = _interopRequireDefault(require("../../../elements/other/headline-lockup/index.js"));
var _lazyVideo = _interopRequireDefault(require("../../../elements/other/lazy-video"));
var _merchButton = require("@site/pattern-library-core/merch-button");
var _inputBar = _interopRequireDefault(require("@site/pattern-library-core/input-bar"));
var _domainSearch = _interopRequireDefault(require("@site/pattern-library-core/domain-search"));
var _textLoopLockup = _interopRequireDefault(require("./common/text-loop-lockup.js"));
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const VideoContent = /*#__PURE__*/(0, _react2.styled)(_section.default)({
  name: "VideoContent",
  class: "pl-vp2r47b",
  vars: {
    "vp2r47b-0": [({
      fitsWrapper
    }) => fitsWrapper ? '100%' : 'var(--video-height)']
  }
});
const Video = /*#__PURE__*/(0, _react2.styled)(_lazyVideo.default)({
  name: "Video",
  class: "pl-vwmrqva",
  vars: {
    "vwmrqva-0": [({
      overlay
    }) => overlay ? `${_intents.default.ux.boxLowContrastOverlay.backgroundColor}` : 'transparent']
  }
});

/**
 * VideoSection component.
 * @module VideoSection
 *
 * @param {Object} props React props
 * @param {string} props.title Heading text
 * @param {string} props.eyebrow Eyebrow text
 * @param {string} props.introUrl Video Intro Url
 * @param {string} props.youTubeId The id of a YouTube Video
 * @param {string} props.lightboxUrl Lightbox Video Url
 * @param {string} props.thumbnailDesktop Fallback image url for desktop devices
 * @param {string} props.thumbnailMobile Fallback image url for mobile devices
 * @param {string} props.ariaLabelCloseButton Aria label for Lightbox close button
 * @param {Object} props.transcriptButton A transcript for the video, an A11y requirement
 * @param {string} props.verticalMargin Vertical Spacing value
 * @param {string} props.words Words for the text loop, separated by '|'.
 * @param {string} props.headlineLoopTextColor Color of text for the TextLoop.
 * @param {Element} props.videos React element with Bynder video props for new Contentful setup
 * @param {Object|Node} props.headlineLockup HeadlineLockup configuration object or custom React component
 * @param {Object|Node} props.priceLockup PriceLockup configuration object or custom React component
 * @returns {React.ReactElement} VideoSection component
 * @public
 */

// eslint-disable-next-line complexity
function VideoSection({
  id,
  _name,
  introUrl,
  introUrlLaptop,
  youTubeId,
  lightboxUrl,
  disableAutoPlay,
  thumbnailDesktop,
  thumbnailMobile,
  ariaLabelCloseButton,
  transcriptButton,
  fitsWrapper,
  portalElem,
  className,
  verticalMargin = 'none',
  headlineLoopTextColor = '#61edea',
  words,
  videos,
  headlineLockup,
  priceLockup,
  ...headlineProps
}) {
  const [showModal, setShowModal] = (0, _react.useState)(false);
  const [fallbackThumbnail, setFallbackThumbnail] = (0, _react.useState)(thumbnailDesktop);
  const {
    width
  } = (0, _patternLibraryUtils.useWindowSize)();
  (0, _react.useEffect)(() => {
    if (width < _patternLibraryUtils.size.tablet) {
      setFallbackThumbnail(thumbnailMobile);
    } else {
      setFallbackThumbnail(thumbnailDesktop);
    }
  }, [width, thumbnailDesktop, thumbnailMobile]);
  const hasValidVideos = /*#__PURE__*/_react.default.isValidElement(videos);
  const bynderVideoSource = (0, _react.useMemo)(() => {
    if (!hasValidVideos) return null;
    return (0, _patternLibraryUtils.extractBynderVideoSources)(videos, {
      thumbnailDesktop,
      thumbnailMobile
    });
  }, [videos, thumbnailDesktop, thumbnailMobile]);
  const legacyVideoSource = {
    fallback: fallbackThumbnail,
    base: introUrl,
    laptop: introUrlLaptop
  };
  const videoSource = hasValidVideos ? bynderVideoSource : legacyVideoSource;
  const defaultCta = (lightboxUrl || youTubeId) && /*#__PURE__*/_react.default.createElement(_playButton.default, {
    id: id,
    handlePlay: () => setShowModal(true)
  });
  const finalCta = headlineLockup?.props?.ctas ? headlineLockup.props.ctas : defaultCta;
  const finalHeadlineLockup = (0, _useElementProps.default)(headlineLockup, {
    ctas: finalCta
  }) || /*#__PURE__*/_react.default.createElement(_index.default, (0, _extends2.default)({
    textAlign: "center",
    ctaFlexDirection: "row",
    ctas: defaultCta
  }, headlineProps));
  const finalPriceLockup = (0, _useElementProps.default)(priceLockup);
  return /*#__PURE__*/_react.default.createElement(VideoContent, {
    padding: "none",
    verticalMargin: verticalMargin,
    dataCy: "video-section",
    _name: _name,
    id: `id-${id}`,
    fitsWrapper: fitsWrapper,
    className: className
  }, /*#__PURE__*/_react.default.createElement("div", {
    style: {
      zIndex: 1
    }
  }, words ? /*#__PURE__*/_react.default.createElement(_textLoopLockup.default, (0, _extends2.default)({}, headlineProps, {
    textColor: headlineLoopTextColor,
    words: words,
    cta: defaultCta,
    videoId: `videoId-${id}`
  })) : finalHeadlineLockup, finalPriceLockup), /*#__PURE__*/_react.default.createElement(Video, {
    id: `videoId-${id}`,
    videoSource: videoSource,
    preload: "auto",
    loop: true,
    autoPlay: true,
    overlay: lightboxUrl || youTubeId
  }), (lightboxUrl || youTubeId) && /*#__PURE__*/_react.default.createElement(_videoLightbox.default, {
    youTubeId,
    lightboxUrl,
    ariaLabelCloseButton,
    transcriptButton,
    showModal,
    handleClose: () => setShowModal(false),
    portalElem,
    disableAutoPlay
  }));
}
VideoSection.propTypes = {
  _name: _propTypes.default.string,
  // Content name from SWP
  id: _propTypes.default.string,
  // Section Id
  introUrl: _propTypes.default.string,
  // Video Intro Url
  youTubeId: _propTypes.default.string,
  // YouTube Video Id (has priority over lightbox URL)
  lightboxUrl: _propTypes.default.string,
  // Lightbox Video Url
  thumbnailDesktop: _propTypes.default.string,
  // fallback thumbnail image on desktop
  thumbnailMobile: _propTypes.default.string,
  // fallback thumbnail image on mobile
  ariaLabelCloseButton: _propTypes.default.string,
  // aria label for lightbox close button
  transcriptButton: _propTypes.default.shape(_merchButton.NSMerchButton.propTypes),
  // a transcript button, A11y requirement
  fitsWrapper: _propTypes.default.bool,
  // flag that allows VideoSection fits the wrapper
  portalElem: _propTypes.default.object,
  // node where LightBox should be rendered
  className: _propTypes.default.string,
  eyebrow: _propTypes.default.string,
  eyebrowTag: _propTypes.default.oneOf(['h1', 'h2', 'h3', 'p']),
  heading: _propTypes.default.node,
  headlineSize: _propTypes.default.oneOf(['d1', 'd2', 'd3', 'h1', 'h2', 'h3', 'h4', 'h5']),
  headlineTag: _propTypes.default.oneOf(['h1', 'h2', 'h3', 'h4', 'p']),
  description: _propTypes.default.node,
  actionsLayout: _propTypes.default.oneOf(['button-set', 'input-bar', 'domain-search', 'none']),
  ctas: _propTypes.default.oneOfType([
  // PropTypes.instanceOf(Actions),
  _propTypes.default.node, _propTypes.default.arrayOf(_propTypes.default.oneOfType([_propTypes.default.shape(_merchButton.NSMerchButton.propTypes), _propTypes.default.shape(_inputBar.default.propTypes), _propTypes.default.shape(_domainSearch.default.propTypes)]))]),
  ctaFlexDirection: _propTypes.default.oneOf(['row', 'column']),
  actionDescription: _propTypes.default.node,
  disclaimer: _propTypes.default.string,
  textAlign: _propTypes.default.oneOf(['start', 'center', 'end']),
  headlineLineLength: _propTypes.default.number,
  tagLabel: _propTypes.default.string,
  labelDesign: _propTypes.default.string,
  videos: _propTypes.default.element,
  // React element with Bynder video props for new Contentful setup
  headlineLockup: _propTypes.default.oneOfType([_propTypes.default.object, _propTypes.default.node]),
  ..._patternLibraryUtils.marginProps
};